import { computed, toValue, type MaybeRef } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { sessionClient } from "@/services/api";
import { queryKeys } from "./queryKeys";
import { useAppStore } from "@/stores/app";
import type {
  PracticeSession,
  PracticeStats,
  ListPracticeSessionsRequestSchema,
  CreatePracticeSessionRequestSchema,
  PracticeSessionSchema,
  GetPracticeStatsRequestSchema,
} from "@/gen/tempus/v1/tempus_pb";
import type { MessageInitShape } from "@bufbuild/protobuf";
import type { FieldMask, Timestamp } from "@bufbuild/protobuf/wkt";
import { normalizeUpdateMask } from "@/utils/updateMask";
import { toTimestamp } from "@/utils/timestamp";

function getStartTime(session: PracticeSession): Timestamp | undefined {
  return session.startTime ?? undefined;
}

function getEndTime(session: PracticeSession): Timestamp | undefined {
  return session.endTime ?? undefined;
}

export function useSessionsQuery(
  params?: MaybeRef<
    MessageInitShape<typeof ListPracticeSessionsRequestSchema> | undefined
  >
) {
  const query = useQuery({
    queryKey: computed(() =>
      queryKeys.sessions.list(
        params ? (toValue(params) as Record<string, unknown>) : undefined
      )
    ),
    queryFn: async () => {
      const response = await sessionClient.listPracticeSessions(
        toValue(params) ?? {}
      );
      return response.sessions ?? [];
    },
  });

  const sessionsSortedByDate = computed<PracticeSession[]>(() => {
    const sessions = query.data.value ?? [];
    return [...sessions].sort((a, b) => {
      const firstStart = getStartTime(b);
      const secondStart = getStartTime(a);
      const firstDate = firstStart
        ? new Date(Number(firstStart.seconds) * 1000)
        : new Date(0);
      const secondDate = secondStart
        ? new Date(Number(secondStart.seconds) * 1000)
        : new Date(0);
      return firstDate.getTime() - secondDate.getTime();
    });
  });

  const sessionById = computed(() => {
    const sessions = query.data.value ?? [];
    return (id: number): PracticeSession | undefined =>
      sessions.find((session) => session.id === id);
  });

  const sessionsByExercise = computed(() => {
    const sessions = query.data.value ?? [];
    return (exerciseId: number): PracticeSession[] =>
      sessions.filter((session) =>
        (session.exercises ?? []).some(
          (exercise) => exercise.exerciseId === exerciseId
        )
      );
  });

  const totalPracticeTime = computed(() => {
    const sessions = query.data.value ?? [];
    return sessions.reduce((total, session) => {
      const start = getStartTime(session);
      const end = getEndTime(session);
      if (start && end) {
        const startMs = Number(start.seconds) * 1000;
        const endMs = Number(end.seconds) * 1000;
        const durationMs = endMs - startMs;
        const pausedSeconds = session.totalPausedSeconds ?? 0;
        const durationMinutes = (durationMs / 1000 - pausedSeconds) / 60;
        return total + durationMinutes;
      }
      return total;
    }, 0);
  });

  return {
    ...query,
    sessions: query.data,
    sessionsSortedByDate,
    sessionById,
    sessionsByExercise,
    totalPracticeTime,
  };
}

export function useSessionQuery(id: MaybeRef<number>) {
  return useQuery({
    queryKey: computed(() => queryKeys.sessions.detail(toValue(id))),
    queryFn: () => sessionClient.getPracticeSession({ id: toValue(id) }),
    enabled: computed(() => !!toValue(id)),
  });
}

export function useActiveSessionQuery() {
  return useQuery({
    queryKey: queryKeys.sessions.active(),
    queryFn: async () => {
      const response = await sessionClient.listPracticeSessions({
        active: true,
      });
      const activeSessions = response.sessions ?? [];
      return activeSessions.length > 0 ? activeSessions[0] : null;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function usePracticeStatsQuery(
  params?: MaybeRef<
    MessageInitShape<typeof GetPracticeStatsRequestSchema> | undefined
  >
) {
  return useQuery({
    queryKey: computed(() =>
      queryKeys.sessions.stats(
        params ? (toValue(params) as Record<string, unknown>) : undefined
      )
    ),
    queryFn: () =>
      sessionClient.getPracticeStats(toValue(params) ?? {}),
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();
  const appStore = useAppStore();

  return useMutation({
    mutationFn: (
      data: MessageInitShape<typeof CreatePracticeSessionRequestSchema>
    ) => sessionClient.createPracticeSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all() });
    },
    onError: (error: Error) => {
      appStore.showErrorMessage(error.message || "Failed to create session");
    },
  });
}

export function useUpdateSession() {
  const queryClient = useQueryClient();
  const appStore = useAppStore();

  return useMutation({
    mutationFn: (variables: {
      id: number;
      session: MessageInitShape<typeof PracticeSessionSchema>;
      updateMask?: string | FieldMask;
    }) =>
      sessionClient.updatePracticeSession({
        id: variables.id,
        session: variables.session,
        updateMask: variables.updateMask
          ? normalizeUpdateMask(variables.updateMask)
          : undefined,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.all() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions.detail(variables.id),
      });
    },
    onError: (error: Error) => {
      appStore.showErrorMessage(error.message || "Failed to update session");
    },
  });
}

export function useDeleteSession() {
  const queryClient = useQueryClient();
  const appStore = useAppStore();

  return useMutation({
    mutationFn: (id: number) => sessionClient.deletePracticeSession({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.stats.practice() });
      queryClient.invalidateQueries({ queryKey: queryKeys.history.all() });
    },
    onError: (error: Error) => {
      appStore.showErrorMessage(error.message || "Failed to delete session");
    },
  });
}

export function usePauseSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      sessionClient.updatePracticeSession({
        id,
        session: {
          paused: true,
          pausedAt: toTimestamp(new Date()),
        },
        updateMask: normalizeUpdateMask("paused,paused_at"),
      }),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions.active(),
      });
    },
  });
}

export function useResumeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      id: number;
      currentSession: PracticeSession;
    }) => {
      const pausedAt = variables.currentSession.pausedAt;
      if (!pausedAt) {
        throw new Error("Session was not paused");
      }
      const pausedAtMs = Number(pausedAt.seconds) * 1000;
      const pausedDuration = Math.floor((Date.now() - pausedAtMs) / 1000);
      const totalPausedSeconds =
        (variables.currentSession.totalPausedSeconds ?? 0) + pausedDuration;

      return sessionClient.updatePracticeSession({
        id: variables.id,
        session: {
          paused: false,
          pausedAt: undefined,
          totalPausedSeconds,
        },
        updateMask: normalizeUpdateMask(
          "paused,paused_at,total_paused_seconds"
        ),
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions.active(),
      });
    },
  });
}
